import { Shield, AlertCircle, Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms.toFixed(0)}ms`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  const mins = Math.floor(ms / 60_000)
  const secs = ((ms % 60_000) / 1000).toFixed(0)
  return `${mins}m ${secs}s`
}

interface ResultSummaryBarProps {
  passCount: number
  failCount: number
  totalTests: number
  /** Number of informational (no pass/fail) tests, shown separately */
  infoCount?: number
  overallPassed: boolean
  executionTimeMs: number
  /** Message when passed, e.g. "Sequence appears random" */
  passMessage: string
  /** Message when failed */
  failMessage: string
  /** Extra key-value detail shown after the message (e.g., "α = 0.01") */
  detail?: string
}

export function ResultSummaryBar({
  passCount,
  failCount,
  totalTests: _totalTests,
  infoCount = 0,
  overallPassed,
  executionTimeMs,
  passMessage,
  failMessage,
  detail,
}: ResultSummaryBarProps) {
  const formalTotal = passCount + failCount
  const passPercent = formalTotal > 0 ? (passCount / formalTotal) * 100 : 100

  return (
    <Card className={cn(
      'border-2',
      overallPassed
        ? 'border-green-500/30 bg-green-500/5 dark:border-green-500/20 dark:bg-green-500/5'
        : 'border-red-500/30 bg-red-500/5 dark:border-red-500/20 dark:bg-red-500/5'
    )} role="status" aria-live="polite" aria-label={`Test results: ${passCount} of ${formalTotal} tests passed`}>
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className={cn(
              'flex items-center justify-center h-12 w-12 rounded-full shrink-0',
              overallPassed
                ? 'bg-green-500/15 text-green-600 dark:text-green-400'
                : 'bg-red-500/15 text-red-600 dark:text-red-400'
            )}>
              {overallPassed ? (
                <Shield className="h-6 w-6" />
              ) : (
                <AlertCircle className="h-6 w-6" />
              )}
            </div>
            <div>
              <p className="text-lg font-semibold" data-testid="results-summary">
                {passCount}/{formalTotal} Tests Passed
                {infoCount > 0 && (
                  <span className="text-sm font-normal text-muted-foreground ml-1">
                    + {infoCount} informational
                  </span>
                )}
              </p>
              <p className="text-sm text-muted-foreground">
                {overallPassed ? passMessage : failMessage}
                {detail && (
                  <>
                    {' '}&middot;{' '}
                    <span className="font-mono">{detail}</span>
                  </>
                )}
                {' '}&middot;{' '}
                <span className="font-mono">
                  <Clock className="h-3 w-3 inline mr-0.5" />
                  {formatDuration(executionTimeMs)}
                </span>
              </p>
            </div>
          </div>
          <div className="w-full sm:w-48">
            <div className="flex justify-between text-xs text-muted-foreground mb-1">
              <span>{passCount} pass</span>
              <span>{failCount} fail</span>
            </div>
            <div className="h-2.5 rounded-full bg-muted overflow-hidden flex">
              <div
                className="h-full bg-green-500 rounded-l-full transition-all"
                style={{ width: `${passPercent}%` }}
              />
              {failCount > 0 && (
                <div
                  className="h-full bg-red-500 rounded-r-full"
                  style={{ width: `${100 - passPercent}%` }}
                />
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
